import { RefObject, useEffect } from 'react';

type Args = {
  selectRef: RefObject<HTMLElement | null>;
  labelRef: RefObject<HTMLElement | null>;
  isVertical: boolean;
  isActive: boolean;
};

/**
 * Horizontal-mode clip-path manager. `.select` contains both the (optional) sticky
 * header and the .label scroll viewport, so a simple `inset()` clip-path either
 * spans the whole element (clipping out the header) or stops short (leaving chip
 * overflow visible at the pill corners). Instead, we emit a two-subpath `path()`
 * that keeps the header rect fully visible AND clips the bottom control-height
 * band to the pill outline drawn by .fake-border.
 *
 * The path is recomputed via ResizeObserver because path() doesn't accept CSS
 * vars/calc — coordinates have to be concrete pixels. Vertical mode is handled
 * by static CSS (`.label.vertical:focus-within { clip-path: inset(...) }`) since
 * its scroll container has no header above it.
 */
const useSelectClipPath = ({ selectRef, labelRef, isVertical, isActive }: Args) => {
  useEffect(() => {
    const el = selectRef.current;
    const labelEl = labelRef.current;
    if (!el || !labelEl) return;

    // don't compute anything if vertical or select is not in an active state
    if (isVertical || !isActive) {
      el.style.clipPath = '';
      return;
    }

    const compute = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      // Pull pillTop/controlHeight from the actual label layout instead of parsing
      // the --custom-select-control-height CSS var: offsetTop/offsetHeight always
      // return resolved pixels regardless of the unit the labelHeight prop uses
      // (rem/em/vh would break a parseFloat-based read). Requires `.select` to be
      // labelEl.offsetParent, which is guaranteed by `.select { position: relative }`.
      const pillTop = labelEl.offsetTop;
      const controlHeight = labelEl.offsetHeight;
      const r = Math.min(100, controlHeight / 2, w / 2);

      // Subpath 1 (header rect, omitted when no header is rendered). Subpath 2
      // (pill) traces the FakeBorder outline clockwise from its top-left arc.
      const headerSubpath = pillTop > 0 ? `M0 0H${w}V${pillTop}H0Z ` : '';
      const pillSubpath =
        `M${r} ${pillTop}` +
        `H${w - r}` +
        `A${r} ${r} 0 0 1 ${w} ${pillTop + r}` +
        `V${h - r}` +
        `A${r} ${r} 0 0 1 ${w - r} ${h}` +
        `H${r}` +
        `A${r} ${r} 0 0 1 0 ${h - r}` +
        `V${pillTop + r}` +
        `A${r} ${r} 0 0 1 ${r} ${pillTop}` +
        `Z`;

      el.style.clipPath = `path('${headerSubpath}${pillSubpath}')`;
    };

    compute();
    // ResizeObserver fires only when dimensions actually change and is natively
    // coalesced to one callback per animation frame, so no manual throttling /
    // rAF wrapper is needed. The observer is torn down on cleanup (focus exit
    // or unmount), so it costs nothing while the select is idle.
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => {
      ro.disconnect();
      el.style.clipPath = '';
    };
  }, [selectRef, labelRef, isVertical, isActive]);
};

export default useSelectClipPath;
